import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, FileCheck2, Receipt } from "lucide-react";

const lines = ["ADAC XML that Councils", "can trust and Consultants", "can deliver faster."];
const ease = [0.22, 1, 0.36, 1];

export default function Hero() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMock = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-36 pb-28 md:pt-44 md:pb-36">
      <motion.div
        style={{
          y: yBg,
          backgroundImage:
            "url('https://images.unsplash.com/photo-1764863713822-e6e01fd53326?auto=format&fit=crop&w=1600&q=60')",
        }}
        className="absolute inset-0 -z-10 opacity-[0.05] bg-cover bg-center"
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#F9F9F8] via-[#F9F9F8]/70 to-[#F9F9F8]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <motion.div style={{ opacity: fade }} className="lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase text-[#0033A0] bg-[#E6EBF5] px-3 py-1.5 rounded-full font-mono"
            >
              ADAC XML Workflow Platform
            </motion.span>

            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-[4.1rem] font-semibold tracking-tighter leading-[1.03] text-zinc-900">
              {lines.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "115%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.85, delay: 0.15 + i * 0.12, ease }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-6 text-lg leading-relaxed text-zinc-600 max-w-2xl"
            >
              Validate, compare, profile, and create ADAC-compliant XML in one reliable workflow.
              Cut resubmissions, raise data quality, and walk into every handover with confidence.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.72 }}
              className="mt-9 flex flex-col sm:flex-row gap-4 sm:items-center"
            >
              <button
                data-testid="hero-book-demo-button"
                onClick={() => navigate("/contact")}
                className="group inline-flex items-center justify-center gap-2 bg-[#0033A0] text-white hover:bg-[#00257A] transition-colors px-7 py-3.5 text-sm font-medium rounded-sm"
              >
                Book a demo
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href="#pricing"
                data-testid="hero-pricing-button"
                className="inline-flex items-center justify-center gap-2 bg-white text-zinc-900 border border-zinc-300 hover:bg-zinc-50 transition-colors px-7 py-3.5 text-sm font-medium rounded-sm"
              >
                See pricing
              </a>
              <a
                href="#how-it-works"
                data-testid="hero-workflow-button"
                className="inline-flex items-center justify-center gap-2 text-zinc-700 hover:text-[#0033A0] transition-colors px-2 py-3.5 text-sm font-medium"
              >
                See the workflow →
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-500"
            >
              <span className="inline-flex items-center gap-2"><ShieldCheck size={16} className="text-[#0033A0]" /> Schema-accurate validation</span>
              <span className="inline-flex items-center gap-2"><FileCheck2 size={16} className="text-[#0033A0]" /> Audit-ready handovers</span>
              <span className="inline-flex items-center gap-2"><Receipt size={16} className="text-[#0033A0]" /> Less than one resubmission</span>
            </motion.div>
          </motion.div>

          <div className="lg:col-span-5">
            <motion.div
              style={{ y: yMock }}
              initial={{ opacity: 0, y: 40, rotateX: 8 }}
              animate={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ duration: 0.9, delay: 0.4, ease }}
              className="bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl overflow-hidden"
            >
              <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-800 bg-zinc-950">
                <span className="h-3 w-3 rounded-full bg-red-500/80" />
                <span className="h-3 w-3 rounded-full bg-yellow-500/80" />
                <span className="h-3 w-3 rounded-full bg-green-500/80" />
                <span className="ml-3 text-xs font-mono text-zinc-500">validation_report.xml</span>
              </div>
              <div className="p-5 font-mono text-[13px] leading-relaxed space-y-1.5">
                <p className="text-zinc-500">{"<adac:Survey schemaVersion=\"6.0\">"}</p>
                <p className="text-green-400">✓ Structure valid — 1,284 nodes</p>
                <p className="text-green-400">✓ Mandatory fields present</p>
                <p className="text-yellow-400">⚠ 3 profile warnings (continuity)</p>
                <p className="text-red-400">✗ 1 invalid observation code</p>
                <div className="pt-2 border-t border-zinc-800 mt-2 flex items-center justify-between">
                  <span className="text-zinc-400">Compliance score</span>
                  <span className="text-white font-semibold">98.4%</span>
                </div>
                <div className="h-1.5 w-full bg-zinc-800 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-[#0033A0] rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: "98%" }}
                    transition={{ duration: 1.2, delay: 1, ease }}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
